import React, { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useHotels } from "@/components/pages/hotels/hooks/useHotels";
import { hotelSubscriptionKeys } from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { billingSettingsService } from "@/services/billing-service";
import { tenantSubscriptionService } from "@/services/tenant-subscription-service";
import { Hotel } from "@/types/hotel";
import { HotelSubscription } from "@/types/hotel-subscription";
import { is404 } from "@/utils/httpErrors";

const HOTEL_LIST_LIMIT = 100;
const PAYMENT_FEATURE_KEYS = new Set([
  "payments",
  "payment",
  "guest_payments",
  "guest_payment",
]);

function normalizeFeatureKey(value?: string | null): string {
  if (!value) {
    return "";
  }

  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

function hasPaymentsFeature(subscription?: HotelSubscription | null): boolean {
  if (!subscription) {
    return false;
  }

  return subscription.features.some((feature) => {
    const normalizedKey = normalizeFeatureKey(feature.key);

    return feature.enabled && PAYMENT_FEATURE_KEYS.has(normalizedKey);
  });
}

interface TenantHotelsStripeSectionProps {
  canWrite?: boolean;
}

export const TenantHotelsStripeSection = ({
  canWrite = true,
}: TenantHotelsStripeSectionProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [openingHotelId, setOpeningHotelId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useHotels({
    currentPage: 1,
    perPage: HOTEL_LIST_LIMIT,
    sortField: "name",
    sortDirection: "asc",
  });

  const hotels = data?.data ?? [];
  const hotelSubscriptionQueries = useQueries({
    queries: hotels.map((hotel) => ({
      queryKey: hotelSubscriptionKeys.detail(hotel.id),
      queryFn: () => tenantSubscriptionService.getHotelSubscription(hotel.id),
      staleTime: 60 * 1000,
      enabled: !isLoading && !isError,
    })),
  });

  const handleOpenStripeSettings = async (hotel: Hotel) => {
    setOpeningHotelId(hotel.id);
    const stripeWindow = window.open("about:blank", "_blank");

    if (stripeWindow) {
      stripeWindow.opener = null;
    }

    try {
      await billingSettingsService.getStripeConnectOnboardingStatus(hotel.id);

      const response =
        await billingSettingsService.createStripeConnectLoginLink(hotel.id);
      const stripeUrl =
        typeof response.url === "string" ? response.url.trim() : "";

      if (!stripeUrl) {
        throw new Error("Stripe login URL is missing");
      }

      if (stripeWindow) {
        stripeWindow.location.replace(stripeUrl);
        stripeWindow.focus();
      } else {
        const fallbackWindow = window.open(stripeUrl, "_blank");

        if (fallbackWindow) {
          fallbackWindow.opener = null;
        }
      }
    } catch (error) {
      stripeWindow?.close();

      if (is404(error)) {
        toast.error(
          t("billing.hotelStripeNotConnected", { hotelName: hotel.name }),
        );
        return;
      }

      toast.error(t("billing.failedToOpenHotelStripe"));
    } finally {
      setOpeningHotelId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-card p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-medium">
          {t("billing.tenantHotelsStripeTitle")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("billing.tenantHotelsStripeDescription")}
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t("billing.loadingTenantHotels")}
        </p>
      ) : null}

      {isError ? (
        <div className="flex flex-col gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">
            {t("billing.failedToLoadTenantHotels")}
          </p>
          <div>
            <Button type="button" variant="outline" onClick={() => refetch()}>
              {t("billing.retry")}
            </Button>
          </div>
        </div>
      ) : null}

      {!isLoading && !isError && hotels.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("billing.noTenantHotels")}
        </p>
      ) : null}

      {!isLoading && !isError && hotels.length > 0 ? (
        <>
          <p className="text-sm text-muted-foreground">
            {t("billing.tenantHotelsStripeHint")}
          </p>

          <div className="flex flex-col divide-y rounded-md border">
            {hotels.map((hotel, index) => {
              const isOpening = openingHotelId === hotel.id;
              const subscriptionQuery = hotelSubscriptionQueries[index];
              const isCheckingSubscription =
                subscriptionQuery?.isLoading || subscriptionQuery?.isPending;
              const canOpenStripeSettings = hasPaymentsFeature(
                subscriptionQuery?.data,
              );
              let action: React.ReactNode = null;

              if (isCheckingSubscription) {
                action = (
                  <div className="h-9 w-[172px] shrink-0" aria-hidden="true" />
                );
              } else if (canOpenStripeSettings) {
                action = (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={openingHotelId !== null || !canWrite}
                    onClick={() => {
                      void handleOpenStripeSettings(hotel);
                    }}
                  >
                    {isOpening
                      ? t("billing.openingStripe")
                      : t("billing.openStripeSettings")}
                  </Button>
                );
              }

              return (
                <div
                  key={hotel.id}
                  className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <p className="truncate text-sm font-medium">{hotel.name}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      {hotel.hotelAddress?.fullAddress ||
                        t("billing.hotelAddressUnavailable")}
                    </p>
                  </div>

                  {action}
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
};
